export type ProblemDetailsTelemetry = {
    instance?: string;
    status?: number;
    title: string;
    traceId?: string;
};

const URL_WITH_QUERY_PATTERN = /(https?:\/\/[^\s?#"'<>]+)(?:\?[^\s#"'<>]*)?(?:#[^\s"'<>]*)?/gi;
const RELATIVE_URL_WITH_QUERY_PATTERN = /(\/[a-z0-9._~!$&'()*+,;=:@%/-]+)(?:\?[^\s#"'<>]*)?(?:#[^\s"'<>]*)?/gi;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LONG_TOKEN_PATTERN = /^[a-z0-9_-]{20,}$/i;
const LOGIN_REQUIRED_CODE = 'login_required';
const LOGIN_REQUIRED_MESSAGE = 'login required';

/** Removes query strings and fragments that may contain credentials or user-entered data. */
export function sanitizeDiagnosticText(value: string): string {
    return value.replace(URL_WITH_QUERY_PATTERN, '$1').replace(RELATIVE_URL_WITH_QUERY_PATTERN, '$1');
}

/** Produces a low-cardinality path without host, query string, identifiers, or email addresses. */
export function normalizeTelemetryPath(value: string | null | undefined): string | undefined {
    if (!value?.trim()) {
        return undefined;
    }

    try {
        const url = new URL(value, 'https://telemetry.invalid');
        return url.pathname
            .split('/')
            .map((segment) => (isIdentifierSegment(segment) ? ':id' : segment))
            .join('/');
    } catch {
        const sanitized = sanitizeDiagnosticText(value);
        return sanitized.split(/[?#]/, 1)[0] || undefined;
    }
}

export function classifyHttpFailure(status: number, statusText?: string): string {
    if (status === 0 && statusText === 'Request Timeout') {
        return 'timeout';
    }
    if (status === 0) {
        return 'network';
    }
    if (status === 401) {
        return 'authentication';
    }
    if (status === 403) {
        return 'authorization';
    }
    if (status === 404) {
        return 'notFound';
    }
    if (status === 409) {
        return 'conflict';
    }
    if (status === 422) {
        return 'validation';
    }
    if (status >= 500) {
        return 'server';
    }
    return 'client';
}

export function asProblemDetails(value: unknown): ProblemDetailsTelemetry | undefined {
    if (!isObjectRecord(value) || typeof value.title !== 'string' || !('status' in value || 'detail' in value || 'traceId' in value)) {
        return undefined;
    }

    return {
        title: value.title,
        status: typeof value.status === 'number' ? value.status : undefined,
        traceId: typeof value.traceId === 'string' ? value.traceId : undefined,
        instance: typeof value.instance === 'string' ? value.instance : undefined
    };
}

/** Identifies Auth0's expected signal that an interactive login must replace silent authentication. */
export function isLoginRequiredError(value: unknown): boolean {
    const unwrapped = unwrapError(value);
    if (!isObjectRecord(unwrapped)) {
        const normalized = normalizeAuthErrorValue(unwrapped);
        return normalized === LOGIN_REQUIRED_CODE || normalized === LOGIN_REQUIRED_MESSAGE;
    }

    const code = normalizeAuthErrorValue(unwrapped['error'] ?? unwrapped['code']);
    const message = normalizeAuthErrorValue(unwrapped['message'] ?? unwrapped['error_description'] ?? unwrapped['value']);
    return code === LOGIN_REQUIRED_CODE || message === LOGIN_REQUIRED_MESSAGE;
}

/** Drops only expected Auth0 login challenges from PostHog's automatic exception stream. */
export function isLoginRequiredExceptionEvent(event: unknown): boolean {
    if (!isObjectRecord(event) || event['event'] !== '$exception') {
        return false;
    }

    const properties = event['properties'];
    if (!isObjectRecord(properties)) {
        return false;
    }

    const exceptionList = properties['$exception_list'];
    return Array.isArray(exceptionList) && exceptionList.some((exception) => isLoginRequiredError(exception));
}

function isIdentifierSegment(segment: string): boolean {
    const decoded = safeDecode(segment);
    return /^\d+$/.test(decoded) || UUID_PATTERN.test(decoded) || decoded.includes('@') || LONG_TOKEN_PATTERN.test(decoded);
}

function safeDecode(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function unwrapError(value: unknown): unknown {
    let current = value;
    const visited = new Set<unknown>();

    while (isObjectRecord(current) && !visited.has(current)) {
        visited.add(current);
        if ('rejection' in current) {
            current = current['rejection'];
            continue;
        }
        if ('error' in current && isObjectRecord(current['error'])) {
            current = current['error'];
            continue;
        }
        break;
    }

    return current;
}

function normalizeAuthErrorValue(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
